import React, { createContext, useState, useContext, useEffect } from 'react';
import { collection, collectionGroup, doc, getDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from './../firebase'; // Assume Firestore instance is imported from here
import { useUser } from './UserContext.js';

const SecondHeaderContext = createContext();

/** Display unit whose unit_ranges includes this physical Firestore unit id. */
const findDisplayUnitForPhysicalUnitId = (displayUnits, physicalUnitId) => {
    if (!displayUnits || !Array.isArray(displayUnits) || physicalUnitId === undefined || physicalUnitId === null) {
        return undefined;
    }
    const idStr = String(physicalUnitId);
    return displayUnits.find((du) =>
        Array.isArray(du?.unit_ranges) &&
        du.unit_ranges.some((r) => r?.unit_id != null && String(r.unit_id) === idStr)
    );
};

/**
 * Prefer deployment_config.classification from the display unit (e.g. class_names) over the Firestore unit doc.
 */
const mergeDisplayUnitClassificationIntoFirestoreUnit = (unit, location, physicalUnitId) => {
    if (!unit) return unit;
    const displayUnit = findDisplayUnitForPhysicalUnitId(location?.display_units, physicalUnitId);
    const displayClassification = displayUnit?.deployment_config?.classification;
    if (!displayClassification) return unit;
    return {
        ...unit,
        deployment_config: {
            ...unit.deployment_config,
            classification: {
                ...unit.deployment_config?.classification,
                ...displayClassification,
            },
        },
    };
};

const reorderUndefinedClassNames = (unit) => {
    if (!unit?.deployment_config?.classification?.class_names) return;
    const classNames = unit.deployment_config.classification.class_names;
    if (!classNames.includes('UNDEFINED')) return;
    const reordered = classNames.filter((cat) => cat !== 'UNDEFINED').concat('UNDEFINED');
    unit.deployment_config.classification = unit.deployment_config.classification || {};
    unit.deployment_config.classification.class_names = reordered;
};

export const useSecondHeader = () => {
    return useContext(SecondHeaderContext);
};

export const SecondHeaderProvider = ({ children }) => {
    const [availableLocations, setAvailableLocations] = useState([]);
    const [currentLocation, setCurrentLocation] = useState(undefined);
    const [currentUnit, setCurrentUnit] = useState(undefined);
    const [firstUse, setFirstUse] = useState(undefined);
    const [latestUseString, setLatestUseString] = useState('');
    const { userData } = useUser();

    const resetCurrentUnit = () => {
        console.log("Setting current unit - 1")
        setCurrentUnit(undefined);
    };

    const resetCurrentLocation = () => {
        setCurrentLocation(undefined);
    };

    const getUnitsData = async (unit_ids) => {
        ////console.log("Firebase - ")
        if (unit_ids.length === 0) return [];

        const q = query(collection(db, 'units'), where('__name__', 'in', unit_ids));
        console.log("Firebase call")
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map(doc => doc.data());
    };

    const getUnitData = async (unit_id) => {
        if (!unit_id) {
            setCurrentUnit(undefined);
            return undefined;
        }

        // Check if unit_id is a number (index for display_units)
        const unitIndex = typeof unit_id === 'string' ? parseInt(unit_id, 10) : unit_id;
        const isNumericIndex = !isNaN(unitIndex) && isFinite(unitIndex) && unitIndex >= 0;

        // If it's a numeric index and we have a location with display_units, get the display_unit
        if (isNumericIndex && currentLocation && currentLocation.display_units && Array.isArray(currentLocation.display_units)) {
            const displayUnit = currentLocation.display_units[unitIndex];
            if (displayUnit) {
                reorderUndefinedClassNames(displayUnit);
                setCurrentUnit(displayUnit);
                const _firstUse = new Date(displayUnit.firstUse);
                console.log("First use date:", _firstUse);
                setFirstUse(_firstUse);
                return displayUnit;
            }
        }

        // Legacy behavior: treat as unit_id and fetch from Firestore
        if (currentUnit && String(currentUnit.unit_id) === String(unit_id)) {
            const merged = mergeDisplayUnitClassificationIntoFirestoreUnit(currentUnit, currentLocation, unit_id);
            reorderUndefinedClassNames(merged);
            setCurrentUnit(merged);
            return merged;
        }
        const docRef = doc(db, 'units', unit_id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
            const unit = mergeDisplayUnitClassificationIntoFirestoreUnit(docSnap.data(), currentLocation, unit_id);
            reorderUndefinedClassNames(unit);

            setCurrentUnit(unit);
            return unit;
        }

        setCurrentUnit(undefined);
        return undefined;
    };

    const getLocationData = async (location_id) => {
        if (!userData) return undefined;
        
        var locationRef;

        locationRef = query(
            collectionGroup(db, 'locations'),
            where('id', '==', location_id)
        );

        console.log("Firebase call 1")
        const querySnapshot = await getDocs(locationRef);

        if (!querySnapshot.empty && querySnapshot.docs[0]?.exists()) {
            const locationData = querySnapshot.docs[0].data();
            setCurrentLocation(locationData);
            return locationData;
        }

        console.log("Location not found");
        setCurrentLocation(undefined);
        return undefined;
    };

    const updateLocations = async () => {
        ////console.log("Updating locations...");
        if (!userData?.companies) return [];

        let temp_locations = [];

        let locationCompanies = userData.location_companies;

        for (let locationCompanyKey in locationCompanies) {
            const locationCompany = locationCompanies[locationCompanyKey];
            const companyId = locationCompany["company"];
            const locationComponent = locationCompany["location"];
            const locationRef = collection(db, `companies/${companyId}/locations`);
            const q = query(locationRef, where('id', '==', locationComponent));
            console.log("Firebase call")
            const querySnapshot = await getDocs(q);
            querySnapshot.forEach((doc) => {
                if (doc.exists()) {
                    temp_locations.push({
                        id: doc.id,
                        ...doc.data(),
                    });
                }
            });
        }

        for (let company of userData.companies) {
            const path = `companies/${company}/locations`;
            
            console.log("Firebase call")
            const querySnapshot = await getDocs(collection(db, path));

            querySnapshot.forEach(doc => {
                let data = doc.data();
                data['id'] = doc.id;
                temp_locations.push(data);
            });
        }

        ////console.log("Setting from updateLocation: ", temp_locations);
        setAvailableLocations(temp_locations);
        return temp_locations;
    };

    return (
        <SecondHeaderContext.Provider value={{
            availableLocations,
            currentLocation,
            currentUnit,
            setCurrentUnit,
            resetCurrentUnit,
            resetCurrentLocation,
            latestUseString,
            getUnitsData,
            getUnitData,
            getLocationData,
            updateLocations,
            firstUse,

            setLatestUseString
        }}>
            {children}
        </SecondHeaderContext.Provider>
    );
};
